import {TrashIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Blankslate as PrimerBlankslate} from '@primer/react/experimental'
import {useMemo} from 'react'

import {ActorAvatar} from './ActorAvatar'
import {ALL_CATEGORIES_SENTINEL} from './CategoryFilter'
import {actorTypeString} from './helpers/actor-type-string'
import {buildActorDescription} from './helpers/build-actor-description'
import {humanizeRoleName} from './helpers/humanize-role-name'
import {type Actor, actorKey, type ActorListEditorEmptyState} from './types'

export interface ActorRowListProps<TActor extends Actor> {
  actors: TActor[]
  readOnly?: boolean
  baseAvatarUrl?: string
  categoryFilter: string
  categoriesAvailable: boolean
  onRemove: (actor: TActor) => void
  renderRowActions?: (actor: TActor) => React.ReactNode
  emptyState?: ActorListEditorEmptyState
}

export function ActorRowList<TActor extends Actor>({
  actors,
  readOnly,
  baseAvatarUrl,
  categoryFilter,
  categoriesAvailable,
  onRemove,
  renderRowActions,
  emptyState,
}: ActorRowListProps<TActor>) {
  const visibleActors = useMemo(() => {
    if (categoryFilter === ALL_CATEGORIES_SENTINEL) return actors
    return actors.filter(a => a.actorCategory === categoryFilter)
  }, [actors, categoryFilter])

  const includeCategoryInDescription = categoryFilter === ALL_CATEGORIES_SENTINEL

  if (visibleActors.length === 0) {
    const heading =
      categoryFilter === ALL_CATEGORIES_SENTINEL
        ? (emptyState?.heading ?? 'No actors have been added.')
        : (emptyState?.filteredHeading?.(categoryFilter) ?? `No ${categoryFilter} actors have been added.`)
    return (
      <PrimerBlankslate>
        <PrimerBlankslate.Heading as="h3">{heading}</PrimerBlankslate.Heading>
      </PrimerBlankslate>
    )
  }

  return (
    <ul>
      {visibleActors.map(actor => {
        const id = actorKey(actor)
        const name = humanizeRoleName(actor)
        const description = buildActorDescription({
          category: actor.actorCategory,
          includeCategory: includeCategoryInDescription,
          fallbackTypeLabel: categoriesAvailable ? undefined : actorTypeString(actor.actorType) || undefined,
          teamHandle: actor.actorType === 'Team' ? actor.name : undefined,
          integrationOwner: actor.actorType === 'Integration' ? actor.owner : undefined,
        })
        return (
          <li key={id} className="Box-row d-flex flex-justify-between flex-items-center py-2">
            <div className="d-flex flex-items-center">
              <ActorAvatar actor={actor} baseUrl={baseAvatarUrl} />
              <div className="ml-2 d-flex flex-items-center gap-2">
                <span className="wb-break-word">{name}</span>
                {description ? <span className="f6 fgColor-muted">{description}</span> : null}
              </div>
            </div>
            {!readOnly && (
              <div className="d-flex flex-items-center gap-2">
                {renderRowActions?.(actor)}
                <IconButton
                  icon={TrashIcon}
                  type="button"
                  aria-label={`Delete ${name}`}
                  size="small"
                  variant="invisible"
                  onClick={() => onRemove(actor)}
                />
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
